import { createContext, useContext, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useBranding } from '../hooks/useBranding'
import { hexOk, tokensDeMarca } from '../utils/contraste'

// ── EL COLOR DE LA CLÍNICA TIÑE LA APP ──────────────────────────────────────────────────────────
//
// Cada clínica elige su color en Ajustes y ya teñía sus facturas y sus presupuestos. Decisión del
// dueño (21-ago-2026): que tiña también la app de gestión, MENÚ LATERAL INCLUIDO. Sus palabras: "no
// es tan importante que nuestra marca permanezca tan omnipresente". Biango sigue arriba del menú y
// en el pie de los documentos; el color de la pantalla es de quien paga la licencia.
//
// 5-oct-2026: durante unas horas la app del equipo fue siempre en el azul de Biango y el color de la
// clínica solo en lo del paciente (t103, 0ccfaff0). Ese mismo día el dueño lo devolvió a esto: «Vamos
// a dejarlo como antes, que cada clínica use su color de marca». De aquella vuelta quedan unos colores
// de Biango para la interfaz como POR DEFECTO —sin color de clínica, y en lo que no es de ninguna
// clínica: Superadmin y las pantallas de entrar (`ENTRADA_DEL_PRODUCTO`)— y el atributo de abajo para
// el informe que se imprime. Esa misma noche el dueño eligió esos colores: #1B2130 («menos llamativo»)
// y #465066 de acento (index.css · --biango-principal, --biango-secundario).
//
// ── Y SU COLOR SECUNDARIO (5-oct-2026) ──────────────────────────────────────────────────────────
// «Añadimos un color secundario para el enlace activo del sidebar y para otras zonas donde se pueda
// usar y contrastar». La clínica lo elige en «Datos y marca» (o lo deja vacío y se calcula de su
// principal) y viaja con el principal por la misma puerta (`useBranding`), con el mismo filtro y en
// las mismas pantallas: se inyecta aquí como `--marca-secundario` y `--marca-sobre-secundario`.
//
// ── POR QUÉ SE INYECTA EN <html> Y NO EN UN DIV CONTENEDOR ──────────────────────────────────────
// El portal del paciente (pages/portal/Portal.jsx) lo hace con un div: pone `--primary` en el estilo
// del contenedor y repinta el subárbol. Ahí funciona porque el portal es UNA pantalla cerrada. Aquí
// no vale, por dos motivos que se ven en cuanto se prueba:
//
//   1. Media app se pinta FUERA de ese div. Los modales, los diálogos y los avisos se montan en
//      `document.body` por portal de React, así que se quedarían fuera del contenedor y saldrían
//      azul Biango dentro de una app teñida. Un color de marca a medias se nota más que no tenerlo.
//   2. Las variables derivadas (--primary-light, --primary-50, el menú…) están declaradas en :root
//      y en html.dark, es decir SOBRE <html>. Una variable de CSS se sustituye en el elemento donde
//      está la declaración: si `--marca-clinica` viviera en un div de dentro, `--primary` seguiría
//      resolviéndose arriba, no la encontraría y caería al azul de siempre. Habría que volver a
//      escribir en JS las cinco derivadas y las cinco del modo oscuro, y el modo oscuro dejaría de
//      salir de la hoja de estilos.
//
// Poniéndolo en <html> —el mismo elemento donde vive :root— se inyecta UN dato y la hoja de estilos
// hace el resto, incluido el modo oscuro. Es también donde ThemeContext pone la clase `dark`, así
// que las dos cosas que tiñen la app viven en el mismo sitio.
//
// ── LA SEGURIDAD ────────────────────────────────────────────────────────────────────────────────
// Este color viene de la base de datos y acaba dentro de una propiedad CSS. El servidor ya lo valida
// al guardarlo, pero lo que llega aquí llega por la red: se vuelve a exigir el hexadecimal de seis
// dígitos (utils/contraste.js) ANTES de tocar nada. Sin esa comprobación, un valor como
// `red; background: url(https://…)` sería escribir CSS en la pantalla de todo el equipo — y ese
// `url()` es además una llamada a un servidor de fuera desde dentro de la app.

const MarcaClinicaContext = createContext(null)

// La lista tiene que ser fija y no salir de lo inyectado: al quitar el color hay que borrar TODAS
// las variables, incluidas las que ya no se calculan. Si se leyera del último cálculo, una variable
// dejaría de borrarse el día que se retire y se quedaría pegada en la pantalla hasta recargar.
const VARIABLES = [
  '--marca-clinica', '--marca-clinica-oscuro', '--marca-clinica-menu',
  '--marca-texto', '--marca-texto-oscuro', '--marca-texto-menu',
  '--marca-secundario', '--marca-sobre-secundario',
]

// Marca en <html> que esta clínica tiene color PRINCIPAL propio. Lo usa la hoja de estilos para lo que
// no se puede resolver con una variable: hoy, el anillo que separa el logotipo de Biango de un menú
// teñido de un color parecido al suyo. Con solo el secundario el menú es el de siempre: no se pone.
const ATRIBUTO = 'data-marca-clinica'

// El color de la clínica para lo que se IMPRIME desde la aplicación (utils/informesExport): el informe
// se pinta en una ventana nueva que no hereda nada, así que lo lee de aquí. No es una variable de CSS.
const ATRIBUTO_DOCUMENTOS = 'data-color-documentos'

// ── LOS PORTALES DE PROFESIONALES EXTERNOS NO SE TIÑEN: SON ZONA BIANGO ────────────────────────
// Regla de marca del dueño (21-ago): la marca se coloca según QUIÉN mira cada pantalla. Donde solo
// mira la clínica manda su color; donde mira un profesional EXTERNO manda Biango, porque el
// anestesista y el referidor trabajan en VARIAS clínicas y son el cliente potencial más directo (un
// referidor es un dentista). Sin esta lista, el proveedor teñía también sus portales —usan
// `var(--primary)` en dos docenas de sitios— y Biango desaparecía justo de las dos pantallas que se
// construyeron para que lo vieran.
//
// Y hay un segundo motivo, más concreto: el anestesista CAMBIA DE CLÍNICA sin recargar la página
// (POST /clinica en su portal). El color se resuelve por DOMINIO, así que no cambia con él: se
// habría quedado viendo el color de la clínica por la que entró mientras trabaja para otra. El
// color es justamente la señal de «en qué clínica estoy»; dando el dato equivocado es peor que no
// estar. En su portal la clínica se dice con palabras, en su selector, que no puede equivocarse.
//
// Y EL FORMULARIO PARA ENVIAR UN PACIENTE (B-599, B-600): «/derivar/<clave>» y «/<direccion>/referidor». Lo abre un profesional
// de fuera, así que también es zona Biango; y sin esto se teñía con el color de la clínica del DOMINIO o de la sesión que hubiera
// abierta en ese navegador, que puede ser OTRA que la del formulario. La clínica del formulario se ve por su logo y su nombre.
const PORTALES_EXTERNOS = ['/anestesista', '/referidor', '/derivar']
const FORMULARIO_DE_UNA_CLINICA = /^\/[a-z0-9-]+\/referidor\/?$/i
// t617 · LA PÁGINA DE PRIVACIDAD DE UNA CLÍNICA (pages/PrivacidadPublica): lleva el color de ESA clínica, el que le manda el servidor
// por su dirección, pintado dentro de la propia página. El de aquí sale del dominio o de la sesión abierta en ese navegador, que
// pueden ser de OTRA clínica: sin esta línea, la página de una se teñiría con el color de la otra.
const PAGINA_PUBLICA_DE_UNA_CLINICA = /^\/[a-z0-9-]+\/privacidad\/?$/i
export const esPortalExterno = (ruta) => PORTALES_EXTERNOS.some(p => ruta === p || ruta.startsWith(p + '/'))
  || FORMULARIO_DE_UNA_CLINICA.test(String(ruta || '')) || PAGINA_PUBLICA_DE_UNA_CLINICA.test(String(ruta || ''))

// ── EL /login DE LA APP ES DE BIANGO. PARA TODOS. ────────────────────────────────────────────
//
//  Palabras del dueño, 2-sep-2026: «ni para esteve joder, el /login de la app es para todos iguales,
//  punto y final. Solo cuando se entra al panel de la clinica es cuando entran en juego los colores
//  de cada clinica».
//
//  Hasta hoy el color salia de `/api/branding-publico`, que responde POR EL DOMINIO. Y como todas
//  las clinicas entran por el MISMO dominio (hoy app.clinicaesteve.es, mañana biango.app), la
//  pantalla de acceso se pintaba con el color de UNA clinica —la del dominio— delante de gente de
//  todas las demas. Es el producto quien recibe a la gente; la clinica empieza al entrar.
//
//  OJO A LO QUE **NO** ENTRA AQUI: las pantallas que ve un PACIENTE sin sesion —el consentimiento
//  que firma, su formulario de alta, el portal, la encuesta, el presupuesto, la tablet de la sala de
//  espera— SI son de la clinica y tienen que seguir con su color y su logo. Ahi el paciente no ha
//  venido a Biango: ha venido a su dentista. Por eso la lista es de las pantallas de la APLICACION
//  antes de entrar, y no «todo lo que no tenga sesion».
// 5-oct-2026: también lo demás que es de Biango y no de una clínica: recuperar la contraseña, el
// asistente de configuración por enlace (lo abre el dueño desde su móvil, sin sesión: el dominio podría
// ser el de otra clínica), la firma del contrato con Biango y Superadmin. Ahí, los colores de Biango para
// la interfaz (#1B2130 y su acento #465066), que son también los de una clínica sin color.
const ENTRADA_DEL_PRODUCTO = ['/login', '/invitacion', '/olvide-mi-contrasena', '/restablecer', '/configurar', '/empezar', '/m', '/a', '/firma-biango', '/superadmin']   // t635 · /m: descargar Biango Migrar por su enlace temporal (de Biango, de ninguna clínica); t654 · /a: un archivo del soporte por su enlace corto
const esEntradaDelProducto = (ruta) => ENTRADA_DEL_PRODUCTO.some(p => ruta === p || ruta.startsWith(p + '/'))

export function MarcaClinicaProvider({ children }) {
  const branding = useBranding()
  const { pathname } = useLocation()
  const color = (hexOk(branding?.color) && !esPortalExterno(pathname) && !esEntradaDelProducto(pathname)) ? branding.color : null
  // EL SECUNDARIO, con el mismo filtro y en las mismas pantallas que el principal. Sin elegir, se calcula
  // del principal (utils/contraste · secundarioDe); sin ninguno de los dos, el de Biango (index.css).
  const secundario = (hexOk(branding?.color_secundario) && !esPortalExterno(pathname) && !esEntradaDelProducto(pathname)) ? branding.color_secundario : null
  // Los valores se calculan una vez por pareja de colores, no en cada pintada: son bucles de contraste
  // y este proveedor envuelve la app entera.
  const tokens = useMemo(() => tokensDeMarca(color, secundario), [color, secundario])

  useEffect(() => {
    const raiz = document.documentElement
    if (color) raiz.setAttribute(ATRIBUTO_DOCUMENTOS, color)
    else raiz.removeAttribute(ATRIBUTO_DOCUMENTOS)
  }, [color])

  useEffect(() => {
    const raiz = document.documentElement
    if (!tokens) {
      // SIN COLOR DE CLÍNICA NO SE INYECTA NADA, y es lo importante: la mayoría de instalaciones no
      // tienen color propio y tienen que seguir viéndose exactamente igual que antes de esto. Al no
      // existir las variables, cada `var(--marca-…, <valor de siempre>)` de index.css cae en su
      // valor de siempre, que son los hexadecimales que ya estaban escritos allí.
      for (const v of VARIABLES) raiz.style.removeProperty(v)
      raiz.removeAttribute(ATRIBUTO)
      return
    }
    // setProperty y no un `style="…"` armado con texto: la interfaz del navegador recibe el nombre y
    // el valor por separado, así que ni siquiera hay una cadena de CSS donde colar un segundo
    // declarante. La comprobación del hexadecimal ya se ha hecho arriba; esto es el segundo cierre.
    for (const [v, valor] of Object.entries(tokens)) raiz.style.setProperty(v, valor)
    // Y las que esta vez no vienen, fuera: una clínica con solo el secundario elegido no trae las del
    // principal, y no puede quedarse con las de la clínica (o la pantalla) de antes.
    for (const v of VARIABLES) if (!(v in tokens)) raiz.style.removeProperty(v)
    if (tokens['--marca-clinica']) raiz.setAttribute(ATRIBUTO, '')
    else raiz.removeAttribute(ATRIBUTO)
  }, [tokens])

  return <MarcaClinicaContext.Provider value={color}>{children}</MarcaClinicaContext.Provider>
}

// El color de la clínica ya saneado, o null si no tiene. Para lo que haya que calcular en JS y no
// se pueda resolver con una variable de CSS (por ejemplo, la rejilla de la agenda, que pinta sus
// colores a mano). Devuelve el color TAL CUAL lo eligió la clínica, sin el ajuste de contraste:
// quien necesite el ajustado tiene las variables de CSS o utils/contraste.js.
export const useMarcaClinica = () => useContext(MarcaClinicaContext)
